import { useEffect, useState, FormEvent, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.js";

interface Influencer {
  id: string; name: string; slug: string; status: string; fictionalAge: number;
  language: string; revision: number; virtualCharacter: boolean; biography?: string;
  referenceAssetId?: string | null; tags: string[];
}
interface GeneratedAsset { id: string; shotId: string; sessionId: string; position: number; createdAt: string; }
interface IdentityVersion { id: string; status: string; created_at: string; approved_at?: string; }
interface Asset { id: string; kind: string; status: string; mime_type: string; original_filename: string; created_at: string; }
interface FamilyMember {
  id: string; name: string; relation: string; fictionalAge: number | null;
  traitsJson: string; referenceAssetId: string | null;
  linkedInfluencerId: string | null; notes: string | null;
  status: string; revision: number;
}
interface OrgInfluencer { id: string; name: string; }
interface SocialAccount {
  accountId: string; platform: string; status: string;
  publicationRoute: string; tokenFingerprint: string | null;
  displayName: string | null; externalId: string | null;
  scopes: string[]; verifiedAt: string | null;
  capabilities: Array<{ key: string; label: string; available: boolean; blockedReason: string | null }> | null;
  avatarUrl?: string | null; revision: number;
}
interface OauthPendingProfile {
  stateId: string; platform: string; influencerId: string;
  externalId: string | null; username: string | null; avatarUrl: string | null;
  scopes: unknown; conflict: { accountId: string; influencerName: string | null } | null;
}
interface Publication {
  id: string; influencer_id: string; account_id: string | null;
  caption: string; state: string;
  planned_at: string | null; planned_tz: string | null;
  position: number; revision: number; created_at: string;
}

const RELATION_LABELS: Record<string, string> = {
  partner: "Pareja", child: "Hijo/a", parent: "Padre/Madre",
  sibling: "Hermano/a", pet: "Mascota", other: "Otro",
};

type Tab = "ficha" | "identidad" | "activos" | "familia" | "cuentas" | "publicaciones" | "estrategia";

export function InfluencerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useAuth();
  const [inf, setInf] = useState<Influencer | null>(null);
  const [tab, setTab] = useState<Tab>("ficha");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", fictionalAge: 25, language: "es", biography: "" });

  // Sub-data
  const [idVersions, setIdVersions] = useState<IdentityVersion[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [wallLoaded, setWallLoaded] = useState(false);
  const [wallLoading, setWallLoading] = useState(false);
  const [wallLightboxIdx, setWallLightboxIdx] = useState<number | null>(null);
  const [refPhotoUrl, setRefPhotoUrl] = useState<string | null>(null);
  const [generatedAssets, setGeneratedAssets] = useState<GeneratedAsset[]>([]);
  const [generatedAssetUrls, setGeneratedAssetUrls] = useState<Record<string, string>>({});
  const [showGenPicker, setShowGenPicker] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [orgInfluencers, setOrgInfluencers] = useState<OrgInfluencer[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ name: "", relation: "partner", fictionalAge: "", description: "", notes: "", linkedInfluencerId: "" });
  const [savingMember, setSavingMember] = useState(false);
  const [deletingMember, setDeletingMember] = useState<Record<string, boolean>>({});
  const [memberRefUrls, setMemberRefUrls] = useState<Record<string, string>>({}); // assetId → url
  const [newTag, setNewTag] = useState("");
  const [savingTag, setSavingTag] = useState(false);

  // Social accounts state
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [socialLoaded, setSocialLoaded] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  const [oauthPending, setOauthPending] = useState<OauthPendingProfile | null>(null);
  const [confirmingOauth, setConfirmingOauth] = useState(false);
  const [disconnectingAccount, setDisconnectingAccount] = useState<Record<string, boolean>>({});
  const oauthCheckedRef = useRef(false);

  // Publications + compositor state
  const [publications, setPublications] = useState<Publication[]>([]);
  const [pubsLoaded, setPubsLoaded] = useState(false);
  const [pubsLoading, setPubsLoading] = useState(false);
  const [showCompositor, setShowCompositor] = useState(false);
  const [compositorAssets, setCompositorAssets] = useState<string[]>([]);
  const [compositorForm, setCompositorForm] = useState({
    accountId: "", caption: "",
    audioMode: "SILENT_EXPORT" as "SILENT_EXPORT" | "LICENSED_TRACK" | "PLATFORM_LATER",
    scheduleDate: "", scheduleTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [creatingPub, setCreatingPub] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get<Influencer>(`/influencers/${id}`)
      .then(i => {
        setInf(i);
        setEditForm({ name: i.name, fictionalAge: i.fictionalAge, language: i.language, biography: i.biography ?? "" });
        if (i.referenceAssetId) {
          api.get<{ downloadUrl: string }>(`/assets/${i.referenceAssetId}`)
            .then(r => setRefPhotoUrl(r.downloadUrl)).catch(() => {});
        }
      })
      .catch(() => setError("No encontrado"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id || tab === "ficha") return;
    if (tab === "identidad") api.get<{ identityVersions: IdentityVersion[] }>(`/influencers/${id}/identity-versions`).then(r => setIdVersions(r.identityVersions ?? [])).catch(() => {});
    if (tab === "activos" && !wallLoaded) {
      setWallLoading(true);
      api.get<{ assets: GeneratedAsset[] }>(`/influencers/${id}/generated-assets`)
        .then(async r => {
          const list = r.assets ?? [];
          setGeneratedAssets(list);
          setWallLoaded(true);
          for (const a of list) {
            api.get<{ downloadUrl: string }>(`/assets/${a.id}`)
              .then(res => setGeneratedAssetUrls(prev => ({ ...prev, [a.id]: res.downloadUrl })))
              .catch(() => {});
          }
        })
        .catch(() => {})
        .finally(() => setWallLoading(false));
    }
    if (tab === "familia") {
      loadFamilyMembers();
      api.get<{ influencers: OrgInfluencer[] }>("/influencers?limit=200")
        .then(r => setOrgInfluencers((r.influencers ?? []).filter(i => i.id !== id)))
        .catch(() => {});
    }
    if (tab === "cuentas" && !socialLoaded) loadSocialAccounts();
    if (tab === "publicaciones" && !pubsLoaded) loadPublications();
    if (tab === "publicaciones" && !socialLoaded) loadSocialAccounts();
  }, [id, tab]);

  async function loadFamilyMembers() {
    if (!id) return;
    try {
      const r = await api.get<{ familyMembers: FamilyMember[] }>(`/influencers/${id}/family-members`);
      const members = r.familyMembers ?? [];
      setFamilyMembers(members);
      // Load reference photo URLs
      for (const m of members) {
        if (m.referenceAssetId && !memberRefUrls[m.referenceAssetId]) {
          const aid = m.referenceAssetId;
          api.get<{ downloadUrl: string }>(`/assets/${aid}`)
            .then(res => setMemberRefUrls(prev => ({ ...prev, [aid]: res.downloadUrl })))
            .catch(() => {});
        }
      }
    } catch { /* ignore */ }
  }

  async function saveFamilyMember(e: FormEvent) {
    e.preventDefault();
    if (!inf) return;
    setSavingMember(true);
    try {
      const traits = addMemberForm.description ? JSON.stringify({ description: addMemberForm.description }) : "{}";
      const member = await api.post<FamilyMember>(`/influencers/${inf.id}/family-members`, {
        name: addMemberForm.name,
        relation: addMemberForm.relation,
        fictionalAge: addMemberForm.fictionalAge ? parseInt(addMemberForm.fictionalAge) : null,
        traitsJson: traits,
        linkedInfluencerId: addMemberForm.linkedInfluencerId || null,
        notes: addMemberForm.notes || null,
      });
      setFamilyMembers(prev => [...prev, member]);
      setShowAddMember(false);
      setAddMemberForm({ name: "", relation: "partner", fictionalAge: "", description: "", notes: "", linkedInfluencerId: "" });
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "Error al guardar");
    } finally {
      setSavingMember(false);
    }
  }

  async function deleteFamilyMember(memberId: string) {
    if (!inf || !window.confirm("¿Eliminar este miembro de la familia?")) return;
    setDeletingMember(prev => ({ ...prev, [memberId]: true }));
    try {
      await api.delete(`/influencers/${inf.id}/family-members/${memberId}`);
      setFamilyMembers(prev => prev.filter(m => m.id !== memberId));
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "Error al eliminar");
    } finally {
      setDeletingMember(prev => { const n = { ...prev }; delete n[memberId]; return n; });
    }
  }

  async function approveVersion(versionId: string) {
    if (!inf) return;
    try {
      await api.post(`/identity-versions/${versionId}/approve`, { notes: "Aprobado desde panel" });
      api.get<{ identityVersions: IdentityVersion[] }>(`/influencers/${inf.id}/identity-versions`)
        .then(r => setIdVersions(r.identityVersions ?? [])).catch(() => {});
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "Error al aprobar");
    }
  }

  async function addTag() {
    if (!inf || !newTag || savingTag) return;
    setSavingTag(true);
    try {
      const r = await api.post<{ tags: string[] }>(`/influencers/${inf.id}/tags`, { tag: newTag });
      setInf(prev => prev ? { ...prev, tags: r.tags } : prev);
      setNewTag("");
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "Error al añadir el tag");
    } finally { setSavingTag(false); }
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!inf) return;
    try {
      const updated = await api.patch<Influencer>(`/influencers/${inf.id}`, {
        ...editForm, _revision: inf.revision
      });
      setInf(updated);
      setEditing(false);
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "Error al guardar");
    }
  }

  async function loadSocialAccounts() {
    if (!id || socialLoading) return;
    setSocialLoading(true);
    try {
      const r = await api.get<{ accounts: SocialAccount[] }>(`/influencers/${id}/social-accounts`);
      setSocialAccounts(r.accounts ?? []);
      setSocialLoaded(true);
    } catch { /* ignore */ } finally { setSocialLoading(false); }
  }

  async function loadPublications() {
    if (!id || pubsLoading) return;
    setPubsLoading(true);
    try {
      const r = await api.get<{ items: Publication[] }>(`/publications?influencerId=${id}&limit=50`);
      setPublications(r.items ?? []);
      setPubsLoaded(true);
    } catch { /* ignore */ } finally { setPubsLoading(false); }
  }

  function openCompositor() {
    setShowCompositor(true);
    if (!wallLoaded && !wallLoading && id) {
      setWallLoading(true);
      api.get<{ assets: GeneratedAsset[] }>(`/influencers/${id}/generated-assets`)
        .then(r => {
          setGeneratedAssets(r.assets ?? []);
          setWallLoaded(true);
          for (const a of r.assets ?? []) {
            api.get<{ downloadUrl: string }>(`/assets/${a.id}`)
              .then(res => setGeneratedAssetUrls(prev => ({ ...prev, [a.id]: res.downloadUrl })))
              .catch(() => {});
          }
        })
        .catch(() => {})
        .finally(() => setWallLoading(false));
    }
  }

  async function submitCompositor(e: React.FormEvent) {
    e.preventDefault();
    if (!inf || creatingPub) return;
    setCreatingPub(true);
    try {
      const pub = await api.post<{ publicationId: string; state: string; revision: number }>(
        "/publications",
        {
          influencerId: inf.id,
          accountId: compositorForm.accountId || null,
          caption: compositorForm.caption,
          audioMode: compositorForm.audioMode,
          assetHashes: compositorAssets,
        }
      );
      if (compositorForm.scheduleDate && pub.publicationId) {
        const localDateTime = compositorForm.scheduleDate.length === 16
          ? compositorForm.scheduleDate + ":00"
          : compositorForm.scheduleDate.slice(0, 19);
        try {
          await api.post(`/publications/${pub.publicationId}/schedule`, {
            localDateTime,
            timezone: compositorForm.scheduleTz,
          });
        } catch { /* schedule error — pub saved as draft */ }
      }
      setShowCompositor(false);
      setCompositorAssets([]);
      setCompositorForm(f => ({ ...f, accountId: "", caption: "", scheduleDate: "" }));
      setPubsLoaded(false);
      loadPublications();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "Error al crear la publicación");
    } finally { setCreatingPub(false); }
  }

  // Check for OAuth result in URL params (called once after inf loads)
  useEffect(() => {
    if (!inf || oauthCheckedRef.current) return;
    const oauthPendingId = searchParams.get("oauth_pending");
    const oauthPlatform = searchParams.get("platform");
    const oauthError = searchParams.get("oauth_error");
    if (oauthPendingId && oauthPlatform) {
      oauthCheckedRef.current = true;
      setTab("cuentas");
      api.get<OauthPendingProfile>(`/social/oauth/${oauthPlatform}/pending/${oauthPendingId}`)
        .then(p => setOauthPending(p))
        .catch(() => {});
      // Clean URL params without navigation
      setSearchParams({}, { replace: true });
    } else if (oauthError) {
      oauthCheckedRef.current = true;
      setTab("cuentas");
      alert(`Error al conectar cuenta: ${oauthError}`);
      setSearchParams({}, { replace: true });
    }
  }, [inf]);

  async function confirmOauth(overrideConflict = false) {
    if (!oauthPending || confirmingOauth) return;
    setConfirmingOauth(true);
    try {
      await api.post(`/social/oauth/${oauthPending.platform}/confirm/${oauthPending.stateId}`, { overrideConflict });
      setOauthPending(null);
      setSocialLoaded(false);
      loadSocialAccounts();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string; code?: string } };
      if (e.data?.code === "ACCOUNT_CONFLICT") {
        if (window.confirm(`Esta cuenta ya está vinculada a otro influencer. ¿Reasignar a ${inf?.name}?`)) {
          setConfirmingOauth(false);
          return confirmOauth(true);
        }
        setOauthPending(null);
      } else {
        alert(e.data?.message || "Error al confirmar");
      }
    } finally { setConfirmingOauth(false); }
  }

  async function disconnectAccount(accountId: string, platform: string) {
    if (!window.confirm(`¿Desconectar cuenta ${platform}?`)) return;
    setDisconnectingAccount(prev => ({ ...prev, [accountId]: true }));
    try {
      await api.post(`/social-accounts/${accountId}/revoke`, {});
      setSocialAccounts(prev => prev.map(a => a.accountId === accountId ? { ...a, status: "revoked" } : a));
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "Error al desconectar");
    } finally { setDisconnectingAccount(prev => { const n = { ...prev }; delete n[accountId]; return n; }); }
  }

  if (loading) return <div style={{ padding: 24, color: "#888" }}>Cargando…</div>;
  if (error || !inf) return (
    <div style={{ padding: 24 }}>
      <div style={{ color: "#dc2626", marginBottom: 12 }}>{error || "No encontrado"}</div>
      <Link to="/influencers" style={{ color: "#2d6a4f" }}>← Volver</Link>
    </div>
  );

  const statusColor = (s: string) => s === "active" ? "#2d6a4f" : s === "paused" ? "#d97706" : "#9ca3af";
  const tabs: Tab[] = ["ficha", "identidad", "activos", "familia", "cuentas", "publicaciones", "estrategia"];
  const PLATFORM_LABELS: Record<string, string> = { instagram: "Instagram", tiktok: "TikTok" };
  const PLATFORM_COLORS: Record<string, string> = { instagram: "#e1306c", tiktok: "#010101" };
  const STATUS_COLORS: Record<string, string> = { connected: "#2d6a4f", disconnected: "#9ca3af", revoked: "#dc2626" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <Link to="/influencers" style={{ color: "#888", textDecoration: "none", fontSize: 13 }}>← Influencers</Link>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{inf.name}</h1>
          <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>
            @{inf.slug} · {inf.fictionalAge} años · {inf.language.toUpperCase()}
            <span style={{ marginLeft: 8, padding: "1px 8px", borderRadius: 12, background: statusColor(inf.status) + "22", color: statusColor(inf.status), fontWeight: 600, fontSize: 11 }}>{inf.status}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {inf.status === "draft" && (role === "owner" || role === "editor") && (
            <button onClick={async () => {
              try {
                const updated = await api.patch<Influencer>(`/influencers/${inf.id}`, { status: "active" }, { "If-Match": String(inf.revision) });
                setInf(updated);
              } catch (err: unknown) {
                const e = err as { data?: { message?: string } };
                alert(e.data?.message || "Error al activar");
              }
            }} style={{ ...btnPrim, background: "#1d4ed8" }}>Activar</button>
          )}
          {(role === "owner" || role === "editor") && !editing && (
            <button onClick={() => setEditing(true)} style={btnSec}>Editar</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: "2px solid #e8e4dc" }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "8px 20px", border: "none", background: "none", cursor: "pointer",
            fontWeight: tab === t ? 600 : 400, color: tab === t ? "#2d6a4f" : "#666",
            borderBottom: tab === t ? "2px solid #2d6a4f" : "2px solid transparent",
            marginBottom: -2, fontSize: 14
          }}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      {/* Ficha tab */}
      {tab === "ficha" && (
        <div>
          {editing ? (
            <form onSubmit={handleSave} style={{ background: "#fff", padding: 24, borderRadius: 10, display: "flex", flexDirection: "column", gap: 12, maxWidth: 500 }}>
              <h3 style={{ margin: 0 }}>Editar ficha</h3>
              <label style={lbl}>Nombre <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} required style={inp} /></label>
              <label style={lbl}>Edad ficticia (≥25) <input type="number" min={25} value={editForm.fictionalAge} onChange={e => setEditForm({ ...editForm, fictionalAge: Number(e.target.value) })} required style={inp} /></label>
              <label style={lbl}>Idioma <select value={editForm.language} onChange={e => setEditForm({ ...editForm, language: e.target.value })} style={inp}>
                <option value="es">Español</option><option value="en">Inglés</option><option value="ca">Catalán</option>
              </select></label>
              <label style={lbl}>Bio <textarea value={editForm.biography} onChange={e => setEditForm({ ...editForm, biography: e.target.value })} style={{ ...inp, height: 80, resize: "vertical" }} /></label>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" style={btnPrim}>Guardar</button>
                <button type="button" onClick={() => setEditing(false)} style={btnSec}>Cancelar</button>
              </div>
            </form>
          ) : (
            <div style={{ background: "#fff", padding: 24, borderRadius: 10, maxWidth: 500 }}>
              <Row label="Nombre" value={inf.name} />
              <Row label="Edad ficticia" value={`${inf.fictionalAge} años`} />
              <Row label="Idioma" value={inf.language.toUpperCase()} />
              <Row label="Estado" value={inf.status} />
              <Row label="Personaje virtual" value={inf.virtualCharacter ? "Sí" : "No"} />
              {inf.biography && <Row label="Bio" value={inf.biography} />}
              <Row label="Revisión" value={String(inf.revision)} />
            </div>
          )}

          {/* Reference photo section */}
          <div style={{ background: "#fff", padding: 24, borderRadius: 10, maxWidth: 500, marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Foto de referencia</h3>
                <p style={{ margin: 0, fontSize: 12, color: "#888" }}>Imagen base que se usará como referencia en las generaciones.</p>
              </div>
              {(role === "owner" || role === "editor") && (
                <div style={{ display: "flex", gap: 8 }}>
                  <label style={{ ...btnSec, cursor: "pointer", opacity: uploadingRef ? 0.6 : 1 }}>
                    {uploadingRef ? "Subiendo…" : "📤 Subir"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/avif" style={{ display: "none" }} disabled={uploadingRef}
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        if (!file || !inf) return;
                        setUploadingRef(true);
                        try {
                          const arrBuf = await file.arrayBuffer();
                          const hashBuf = await crypto.subtle.digest("SHA-256", arrBuf);
                          const sha256 = Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, "0")).join("");
                          const initRes = await api.post<{ uploadId: string; uploadUrl: string }>(
                            "/uploads/init", { kind: "image", mime: file.type, bytes: String(file.size) }
                          );
                          // POST instead of PUT — Apache strips PUT bodies through the reverse proxy
                          await fetch(initRes.uploadUrl, { method: "POST", body: new Blob([arrBuf]), headers: { "Content-Type": "application/octet-stream" }, credentials: "include" });
                          await api.post(`/uploads/${initRes.uploadId}/complete`, { sha256 });
                          const updated = await api.patch<Influencer>(`/influencers/${inf.id}`, { referenceAssetId: initRes.uploadId }, { "If-Match": String(inf.revision) });
                          setInf(updated);
                          setRefPhotoUrl(URL.createObjectURL(file));
                        } catch (err: unknown) {
                          const e = err as { data?: { message?: string } };
                          alert(e.data?.message || "Error al subir la foto");
                        } finally { setUploadingRef(false); e.target.value = ""; }
                      }}
                    />
                  </label>
                  <button style={btnSec} onClick={() => {
                    setShowGenPicker(p => !p);
                    if (!showGenPicker && inf) {
                      api.get<{ assets: GeneratedAsset[] }>(`/influencers/${inf.id}/generated-assets`)
                        .then(r => {
                          setGeneratedAssets(r.assets ?? []);
                          for (const a of (r.assets ?? [])) {
                            api.get<{ downloadUrl: string }>(`/assets/${a.id}`)
                              .then(u => setGeneratedAssetUrls(prev => ({ ...prev, [a.id]: u.downloadUrl }))).catch(() => {});
                          }
                        }).catch(() => {});
                    }
                  }}>🖼 De tomas</button>
                  {inf.referenceAssetId && (
                    <button style={{ ...btnSec, color: "#dc2626", borderColor: "#fca5a5" }} onClick={async () => {
                      if (!inf || !window.confirm("¿Quitar la foto de referencia?")) return;
                      try {
                        const updated = await api.patch<Influencer>(`/influencers/${inf.id}`, { referenceAssetId: null, _revision: inf.revision }, { "If-Match": String(inf.revision) });
                        setInf(updated); setRefPhotoUrl(null);
                      } catch { alert("Error al quitar la foto"); }
                    }}>✕ Quitar</button>
                  )}
                </div>
              )}
            </div>

            {refPhotoUrl ? (
              <div style={{ position: "relative", display: "inline-block" }}>
                <img src={refPhotoUrl} alt="Foto de referencia" style={{ maxHeight: 280, maxWidth: "100%", borderRadius: 8, objectFit: "contain", border: "2px solid #2d6a4f", display: "block" }} />
                <button onClick={() => setLightboxUrl(refPhotoUrl)} title="Ver en grande"
                  style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%", width: 28, height: 28, cursor: "pointer", color: "#fff", fontSize: 14, lineHeight: "28px", textAlign: "center", padding: 0 }}>🔍</button>
              </div>
            ) : (
              <div style={{ border: "2px dashed #d1d5db", borderRadius: 8, padding: "32px 24px", textAlign: "center", color: "#aaa" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📷</div>
                <p style={{ margin: 0, fontSize: 13 }}>Sin foto de referencia. Sube una imagen o elige una de las tomas generadas.</p>
              </div>
            )}

            {/* Picker: generated shots */}
            {showGenPicker && (
              <div style={{ marginTop: 16 }}>
                <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 600, color: "#444" }}>Tomas generadas — elige una como referencia:</p>
                {generatedAssets.length === 0 ? (
                  <p style={{ fontSize: 13, color: "#aaa" }}>Sin imágenes generadas todavía.</p>
                ) : (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {generatedAssets.map(a => {
                      const url = generatedAssetUrls[a.id];
                      const isCurrent = inf.referenceAssetId === a.id;
                      return (
                        <div key={a.id} style={{ position: "relative", cursor: url ? "pointer" : "default" }}
                          onClick={async () => {
                            if (!url || !inf || isCurrent) return;
                            try {
                              const updated = await api.patch<Influencer>(`/influencers/${inf.id}`, { referenceAssetId: a.id, _revision: inf.revision }, { "If-Match": String(inf.revision) });
                              setInf(updated); setRefPhotoUrl(url); setShowGenPicker(false);
                            } catch { alert("Error al establecer la referencia"); }
                          }}
                        >
                          {url
                            ? <img src={url} alt={`Toma ${a.position + 1}`} style={{ width: 72, height: 96, objectFit: "cover", borderRadius: 6, border: isCurrent ? "3px solid #2d6a4f" : "2px solid #e5e7eb", display: "block" }} />
                            : <div style={{ width: 72, height: 96, borderRadius: 6, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>⏳</div>
                          }
                          {isCurrent && <div style={{ position: "absolute", top: 4, right: 4, background: "#2d6a4f", borderRadius: "50%", width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff" }}>✓</div>}
                          <div style={{ fontSize: 10, color: "#888", textAlign: "center", marginTop: 2 }}>#{a.position + 1}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tags section */}
          <div style={{ background: "#fff", padding: 24, borderRadius: 10, maxWidth: 500, marginTop: 16 }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Intereses y tags</h3>
            <p style={{ margin: "0 0 14px", fontSize: 12, color: "#888" }}>Tags para clasificar gustos, estilos o nichos. Se usan para filtrar productos del catálogo.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: inf?.tags?.length ? 12 : 0 }}>
              {(inf.tags ?? []).map(tag => (
                <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, background: "#d8f3dc", color: "#2d6a4f", fontSize: 12, fontWeight: 600 }}>
                  {tag}
                  {(role === "owner" || role === "editor") && (
                    <button onClick={async () => {
                      if (!inf) return;
                      try {
                        const r = await api.delete<{ tags: string[] }>(`/influencers/${inf.id}/tags/${tag}`);
                        setInf(prev => prev ? { ...prev, tags: r.tags } : prev);
                      } catch { alert("Error al eliminar el tag"); }
                    }} style={{ background: "none", border: "none", cursor: "pointer", color: "#2d6a4f", fontSize: 12, padding: 0, lineHeight: 1, fontWeight: 700 }}>×</button>
                  )}
                </span>
              ))}
              {(inf.tags ?? []).length === 0 && <span style={{ fontSize: 12, color: "#aaa" }}>Sin tags todavía.</span>}
            </div>
            {(role === "owner" || role === "editor") && (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <input
                  value={newTag}
                  onChange={e => setNewTag(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                  onKeyDown={async e => { if (e.key === "Enter") { e.preventDefault(); await addTag(); } }}
                  placeholder="moda, lifestyle, fitness…"
                  maxLength={50}
                  style={{ ...inp, flex: 1, fontSize: 12 }}
                />
                <button disabled={savingTag || !newTag} onClick={addTag}
                  style={{ ...btnPrim, fontSize: 12, padding: "6px 14px", opacity: !newTag ? 0.5 : 1 }}>
                  {savingTag ? "…" : "Añadir"}
                </button>
              </div>
            )}
          </div>

          {/* Lightbox */}
          {lightboxUrl && (
            <div onClick={() => setLightboxUrl(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}>
              <img src={lightboxUrl} alt="Vista ampliada" onClick={e => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.6)", cursor: "default" }} />
              <button onClick={() => setLightboxUrl(null)} style={{ position: "fixed", top: 20, right: 24, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", color: "#fff", fontSize: 20, lineHeight: "40px", textAlign: "center", padding: 0 }}>✕</button>
            </div>
          )}
        </div>
      )}

      {/* Identidad tab */}
      {tab === "identidad" && (
        <div>
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Versiones de identidad</h3>
            {(role === "owner" || role === "editor") && (
              <Link to={`/influencers/${id}/identity/new`} style={btnLinkStyle}>+ Nueva versión</Link>
            )}
          </div>
          {idVersions.length === 0 ? (
            <div style={{ color: "#888", background: "#fff", padding: 24, borderRadius: 10 }}>Sin versiones de identidad. Las referencias fotográficas dan coherencia visual al personaje.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {idVersions.map(v => (
                <div key={v.id} style={{ background: "#fff", padding: "12px 16px", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontSize: 13, color: "#555", fontFamily: "monospace" }}>{v.id.slice(0, 8)}…</span>
                    <span style={{ marginLeft: 10, fontSize: 12, padding: "2px 8px", borderRadius: 12, background: v.status === "approved" ? "#d8f3dc" : "#fef9c3", color: v.status === "approved" ? "#2d6a4f" : "#92400e", fontWeight: 600 }}>{v.status}</span>
                  </div>
                  {v.status !== "approved" && (role === "owner" || role === "editor") && (
                    <button onClick={() => approveVersion(v.id)} style={{ padding: "4px 12px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                      Aprobar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activos tab — muro de publicaciones */}
      {tab === "activos" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: "0 0 2px", fontSize: 16 }}>Muro de publicaciones</h3>
              <p style={{ margin: 0, fontSize: 12, color: "#888" }}>{generatedAssets.length} imagen{generatedAssets.length !== 1 ? "es" : ""} generada{generatedAssets.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          {wallLoading ? (
            <div style={{ color: "#888", padding: 24 }}>Cargando…</div>
          ) : generatedAssets.length === 0 ? (
            <div style={{ color: "#888", background: "#fff", padding: 40, borderRadius: 10, textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📸</div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Sin publicaciones todavía</div>
              <div style={{ fontSize: 13 }}>Las imágenes generadas en sesiones aparecerán aquí.</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
              {generatedAssets.map((a, idx) => {
                const url = generatedAssetUrls[a.id];
                return (
                  <div
                    key={a.id}
                    onClick={() => setWallLightboxIdx(idx)}
                    style={{ position: "relative", aspectRatio: "3/4", background: "#e5e7eb", overflow: "hidden", cursor: "zoom-in", borderRadius: 4 }}
                    onMouseEnter={e => { const o = e.currentTarget.querySelector<HTMLElement>(".wo"); if (o) o.style.opacity = "1"; }}
                    onMouseLeave={e => { const o = e.currentTarget.querySelector<HTMLElement>(".wo"); if (o) o.style.opacity = "0"; }}
                  >
                    {url ? (
                      <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block" }} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "#9ca3af" }}>⏳</div>
                    )}
                    <div className="wo" style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.15s" }}>
                      <span style={{ fontSize: 28, color: "#fff" }}>🔍</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Wall lightbox */}
          {wallLightboxIdx !== null && (() => {
            const a = generatedAssets[wallLightboxIdx];
            const url = a ? generatedAssetUrls[a.id] : null;
            const total = generatedAssets.length;
            const prev = wallLightboxIdx > 0 ? wallLightboxIdx - 1 : total - 1;
            const next = wallLightboxIdx < total - 1 ? wallLightboxIdx + 1 : 0;
            return (
              <div
                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
                onClick={() => setWallLightboxIdx(null)}
              >
                {/* Prev */}
                <button
                  onClick={e => { e.stopPropagation(); setWallLightboxIdx(prev); }}
                  style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 48, height: 48, cursor: "pointer", fontSize: 22, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}
                >‹</button>

                {/* Image */}
                <div onClick={e => e.stopPropagation()} style={{ maxWidth: "min(90vw, 600px)", maxHeight: "90vh", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  {url ? (
                    <img src={url} alt="" style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: 6, boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }} />
                  ) : (
                    <div style={{ width: 300, height: 400, background: "#333", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: 32 }}>⏳</div>
                  )}
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <span style={{ color: "#ccc", fontSize: 13 }}>{wallLightboxIdx + 1} / {total}</span>
                    {a && (
                      <Link to={`/sessions/${a.sessionId}`} onClick={() => setWallLightboxIdx(null)}
                        style={{ color: "#93c5fd", fontSize: 12, textDecoration: "none" }}>
                        → Ver sesión
                      </Link>
                    )}
                  </div>
                </div>

                {/* Next */}
                <button
                  onClick={e => { e.stopPropagation(); setWallLightboxIdx(next); }}
                  style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 48, height: 48, cursor: "pointer", fontSize: 22, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}
                >›</button>

                {/* Close */}
                <button
                  onClick={() => setWallLightboxIdx(null)}
                  style={{ position: "absolute", top: 16, right: 20, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 36, height: 36, cursor: "pointer", fontSize: 18, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}
                >✕</button>
              </div>
            );
          })()}
        </div>
      )}

      {/* Familia tab */}
      {tab === "familia" && (
        <div>
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Familia virtual</h3>
              <p style={{ margin: 0, fontSize: 13, color: "#666" }}>
                Define pareja, hijos, mascotas u otros miembros para incluirlos en tomas y crear contenido más realista.
              </p>
            </div>
            {(role === "owner" || role === "editor") && !showAddMember && (
              <button onClick={() => setShowAddMember(true)} style={btnPrim}>+ Añadir miembro</button>
            )}
          </div>

          {showAddMember && (
            <form onSubmit={saveFamilyMember} style={{ background: "#fff", padding: 20, borderRadius: 10, marginBottom: 16, display: "flex", flexDirection: "column", gap: 12, maxWidth: 500 }}>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Nuevo miembro de la familia</h4>
              <label style={lbl}>Nombre *
                <input value={addMemberForm.name} onChange={e => setAddMemberForm(f => ({ ...f, name: e.target.value }))} required style={inp} placeholder="Carlos, Emma, Toby…" />
              </label>
              <label style={lbl}>Relación *
                <select value={addMemberForm.relation} onChange={e => setAddMemberForm(f => ({ ...f, relation: e.target.value }))} style={inp}>
                  <option value="partner">Pareja</option>
                  <option value="child">Hijo/a</option>
                  <option value="parent">Padre/Madre</option>
                  <option value="sibling">Hermano/a</option>
                  <option value="pet">Mascota</option>
                  <option value="other">Otro</option>
                </select>
              </label>
              <label style={lbl}>Edad aproximada (opcional)
                <input type="number" min={0} max={120} value={addMemberForm.fictionalAge} onChange={e => setAddMemberForm(f => ({ ...f, fictionalAge: e.target.value }))} style={inp} placeholder="25" />
              </label>
              <label style={lbl}>Descripción física
                <textarea
                  value={addMemberForm.description}
                  onChange={e => setAddMemberForm(f => ({ ...f, description: e.target.value }))}
                  style={{ ...inp, height: 80, resize: "vertical" }}
                  placeholder="Hombre de complexión atlética, pelo castaño oscuro, ojos verdes, estilo casual…"
                />
              </label>
              <label style={lbl}>Notas adicionales
                <textarea
                  value={addMemberForm.notes}
                  onChange={e => setAddMemberForm(f => ({ ...f, notes: e.target.value }))}
                  style={{ ...inp, height: 50, resize: "vertical" }}
                  placeholder="Ej: suele aparecer en playa y deportes"
                />
              </label>
              {orgInfluencers.length > 0 && (
                <label style={lbl}>Vincular a influencer existente (opcional)
                  <select value={addMemberForm.linkedInfluencerId} onChange={e => setAddMemberForm(f => ({ ...f, linkedInfluencerId: e.target.value }))} style={inp}>
                    <option value="">— Sin vincular —</option>
                    {orgInfluencers.map(i => (
                      <option key={i.id} value={i.id}>{i.name}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Si este miembro es un influencer creado, vincúlalos para habilitar publicaciones conjuntas.</span>
                </label>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" disabled={savingMember} style={{ ...btnPrim, opacity: savingMember ? 0.6 : 1 }}>{savingMember ? "Guardando…" : "Guardar"}</button>
                <button type="button" onClick={() => setShowAddMember(false)} style={btnSec}>Cancelar</button>
              </div>
            </form>
          )}

          {familyMembers.length === 0 && !showAddMember ? (
            <div style={{ background: "#fff", padding: 32, borderRadius: 10, textAlign: "center", color: "#888" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>👨‍👩‍👧‍👦</div>
              <p style={{ margin: "0 0 16px" }}>Sin miembros de la familia todavía.</p>
              <p style={{ margin: 0, fontSize: 13 }}>Añade una pareja, hijos o mascotas para poder incluirlos en las tomas generadas.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {familyMembers.map(m => {
                const traits = (() => { try { return JSON.parse(m.traitsJson) as { description?: string }; } catch { return {}; } })();
                const refUrl = m.referenceAssetId ? memberRefUrls[m.referenceAssetId] : null;
                return (
                  <div key={m.id} style={{ background: "#fff", borderRadius: 10, padding: 16, display: "flex", gap: 14, alignItems: "flex-start" }}>
                    {refUrl ? (
                      <img src={refUrl} alt={m.name} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, flexShrink: 0, border: "2px solid #e5e7eb" }} />
                    ) : (
                      <div style={{ width: 64, height: 64, borderRadius: 8, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>
                        {m.relation === "pet" ? "🐾" : m.relation === "child" ? "👶" : m.relation === "partner" ? "💑" : m.relation === "sibling" ? "🫂" : m.relation === "parent" ? "🧑‍🦳" : "👤"}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{m.name}</span>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: "#f3f4f6", color: "#555", fontWeight: 500 }}>
                          {RELATION_LABELS[m.relation] ?? m.relation}
                        </span>
                        {m.fictionalAge != null && (
                          <span style={{ fontSize: 12, color: "#888" }}>{m.fictionalAge} años</span>
                        )}
                        {m.linkedInfluencerId && (() => {
                          const linked = orgInfluencers.find(i => i.id === m.linkedInfluencerId);
                          return linked ? (
                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: "#dbeafe", color: "#1d4ed8", fontWeight: 500 }}>
                              🔗 {linked.name}
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: "#dbeafe", color: "#1d4ed8", fontWeight: 500 }}>🔗 Vinculado</span>
                          );
                        })()}
                      </div>
                      {traits.description && (
                        <p style={{ margin: "0 0 4px", fontSize: 13, color: "#555", lineHeight: 1.5 }}>{traits.description}</p>
                      )}
                      {m.notes && (
                        <p style={{ margin: 0, fontSize: 12, color: "#888", fontStyle: "italic" }}>{m.notes}</p>
                      )}
                    </div>
                    {(role === "owner" || role === "editor") && (
                      <button
                        onClick={() => void deleteFamilyMember(m.id)}
                        disabled={deletingMember[m.id]}
                        title="Eliminar"
                        style={{ background: "none", border: "none", color: "#bbb", cursor: "pointer", fontSize: 16, padding: "2px 4px", flexShrink: 0 }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = "#e53e3e"; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = "#bbb"; }}
                      >
                        {deletingMember[m.id] ? "…" : "✕"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Cuentas sociales tab */}
      {tab === "cuentas" && (
        <div>
          {/* OAuth confirmation panel */}
          {oauthPending && (
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: 20, marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
                Confirmar vinculación de cuenta
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
                {oauthPending.avatarUrl && (
                  <img src={oauthPending.avatarUrl} alt="avatar" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover" }} />
                )}
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    @{oauthPending.username} · {PLATFORM_LABELS[oauthPending.platform] ?? oauthPending.platform}
                  </div>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    ID externo: {oauthPending.externalId}
                  </div>
                  {oauthPending.conflict && (
                    <div style={{ fontSize: 12, color: "#d97706", marginTop: 4 }}>
                      Conflicto: esta cuenta ya está vinculada a <strong>{oauthPending.conflict.influencerName ?? "otro influencer"}</strong>.
                      Al confirmar se reasignará a <strong>{inf.name}</strong>.
                    </div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 16 }}>
                ¿Vincular esta cuenta a <strong>{inf.name}</strong>?
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => confirmOauth(!!oauthPending.conflict)}
                  disabled={confirmingOauth}
                  style={{ ...btnPrim, opacity: confirmingOauth ? 0.6 : 1 }}
                >
                  {confirmingOauth ? "Confirmando…" : "Confirmar vinculación"}
                </button>
                <button onClick={() => setOauthPending(null)} style={btnSec}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Existing accounts */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
            {socialLoading && <div style={{ color: "#888", fontSize: 13 }}>Cargando cuentas…</div>}
            {!socialLoading && socialAccounts.length === 0 && (
              <div style={{ color: "#aaa", fontSize: 13 }}>No hay cuentas sociales vinculadas.</div>
            )}
            {socialAccounts.map(acc => {
              const caps = Array.isArray(acc.capabilities) ? acc.capabilities : [];
              const statusColor = STATUS_COLORS[acc.status] ?? "#9ca3af";
              const platformColor = PLATFORM_COLORS[acc.platform] ?? "#888";
              return (
                <div key={acc.accountId} style={{
                  background: "#fff", border: "1px solid #e8e4dc", borderRadius: 10,
                  padding: 16, minWidth: 240, maxWidth: 300, flex: "0 0 auto",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 6,
                      background: platformColor, color: "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700, flexShrink: 0,
                    }}>
                      {(PLATFORM_LABELS[acc.platform] ?? acc.platform).slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{PLATFORM_LABELS[acc.platform] ?? acc.platform}</div>
                      <div style={{ fontSize: 11, color: statusColor, fontWeight: 600 }}>{acc.status.toUpperCase()}</div>
                    </div>
                  </div>
                  {acc.displayName && (
                    <div style={{ fontSize: 13, marginBottom: 4 }}>@{acc.displayName}</div>
                  )}
                  {acc.externalId && (
                    <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>ID: {acc.externalId}</div>
                  )}
                  {acc.verifiedAt && (
                    <div style={{ fontSize: 11, color: "#aaa", marginBottom: 8 }}>
                      Verificado: {new Date(acc.verifiedAt).toLocaleDateString("es-ES")}
                    </div>
                  )}
                  {caps.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      {caps.map(c => (
                        <div key={c.key} style={{ fontSize: 11, marginBottom: 2, display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{ color: c.available ? "#2d6a4f" : "#d97706" }}>{c.available ? "✓" : "○"}</span>
                          <span style={{ color: c.available ? "#1a1a1a" : "#aaa" }}>{c.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    {acc.status !== "revoked" && (role === "owner" || role === "editor") && (
                      <>
                        <a
                          href={`/api/v1/social/oauth/${acc.platform}/start?influencerId=${inf.id}`}
                          style={{ ...btnSec, fontSize: 11, padding: "4px 10px", textDecoration: "none" }}
                        >
                          Reconectar
                        </a>
                        <button
                          onClick={() => disconnectAccount(acc.accountId, acc.platform)}
                          disabled={!!disconnectingAccount[acc.accountId]}
                          style={{ padding: "4px 10px", border: "1px solid #fca5a5", borderRadius: 5,
                            cursor: "pointer", background: "none", fontSize: 11, color: "#dc2626" }}
                        >
                          {disconnectingAccount[acc.accountId] ? "…" : "Desconectar"}
                        </button>
                      </>
                    )}
                    {acc.status === "revoked" && (role === "owner" || role === "editor") && (
                      <a
                        href={`/api/v1/social/oauth/${acc.platform}/start?influencerId=${inf.id}`}
                        style={{ ...btnPrim, fontSize: 11, padding: "4px 10px", textDecoration: "none" }}
                      >
                        Volver a conectar
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Connect new account */}
          {(role === "owner" || role === "editor") && (
            <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Conectar nueva cuenta</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(["instagram", "tiktok"] as const).map(platform => {
                  const existing = socialAccounts.find(a => a.platform === platform && a.status === "connected");
                  return (
                    <a
                      key={platform}
                      href={`/api/v1/social/oauth/${platform}/start?influencerId=${inf.id}`}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "8px 16px", borderRadius: 6, textDecoration: "none", fontSize: 13, fontWeight: 600,
                        background: PLATFORM_COLORS[platform], color: "#fff",
                        opacity: existing ? 0.5 : 1,
                      }}
                      title={existing ? `${PLATFORM_LABELS[platform]} ya conectado` : `Conectar ${PLATFORM_LABELS[platform]}`}
                    >
                      {PLATFORM_LABELS[platform]}
                      {existing && <span style={{ fontSize: 11, fontWeight: 400 }}>(ya conectado)</span>}
                    </a>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 10 }}>
                Se te redirigirá a la plataforma para autorizar el acceso. Volverás aquí para confirmar la vinculación.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Publicaciones tab */}
      {tab === "publicaciones" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Publicaciones</h3>
              <p style={{ margin: 0, fontSize: 13, color: "#666" }}>Borradores, programadas e históricas para este influencer.</p>
            </div>
            {(role === "owner" || role === "editor") && !showCompositor && (
              <button onClick={openCompositor} style={btnPrim}>+ Nueva publicación</button>
            )}
          </div>

          {!socialLoading && socialAccounts.filter(a => a.status === "connected").length === 0 && (
            <div style={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 16px", marginBottom: 16, fontSize: 13, color: "#92400e" }}>
              Sin cuentas sociales conectadas. Puedes guardar borradores, pero para programar necesitas vincular una cuenta en la pestaña Cuentas.
            </div>
          )}

          {/* Compositor form */}
          {showCompositor && (
            <form onSubmit={submitCompositor} style={{ background: "#fff", border: "1px solid #86efac", borderRadius: 10, padding: 20, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Nueva publicación</h4>
                <button type="button" onClick={() => { setShowCompositor(false); setCompositorAssets([]); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 18, lineHeight: 1 }}>✕</button>
              </div>

              {/* Account selector */}
              <label style={lbl}>Cuenta de destino
                <select value={compositorForm.accountId} onChange={e => setCompositorForm(f => ({ ...f, accountId: e.target.value }))} style={inp}>
                  <option value="">— Sin cuenta (borrador sin plataforma) —</option>
                  {socialAccounts.filter(a => a.status === "connected").map(a => (
                    <option key={a.accountId} value={a.accountId}>
                      {PLATFORM_LABELS[a.platform] ?? a.platform} · @{a.displayName ?? a.externalId}
                    </option>
                  ))}
                </select>
              </label>

              {/* Asset picker */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 13, color: "#444", fontWeight: 500, marginBottom: 8 }}>
                  Assets seleccionados ({compositorAssets.length})
                  {wallLoading && <span style={{ fontSize: 11, color: "#888", marginLeft: 8 }}>Cargando imágenes…</span>}
                </div>
                {compositorAssets.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                    {compositorAssets.map((assetId, idx) => {
                      const url = generatedAssetUrls[assetId];
                      return (
                        <div key={assetId} style={{ position: "relative" }}>
                          {url
                            ? <img src={url} alt="" style={{ width: 60, height: 80, objectFit: "cover", borderRadius: 4, border: "2px solid #2d6a4f", display: "block" }} />
                            : <div style={{ width: 60, height: 80, borderRadius: 4, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#9ca3af" }}>⏳</div>
                          }
                          <div style={{ position: "absolute", top: 2, left: 2, background: "rgba(0,0,0,0.6)", color: "#fff", borderRadius: 3, fontSize: 10, padding: "1px 4px", lineHeight: 1.4 }}>{idx + 1}</div>
                          <button type="button" onClick={() => setCompositorAssets(prev => prev.filter(a => a !== assetId))}
                            style={{ position: "absolute", top: 2, right: 2, background: "rgba(220,38,38,0.85)", border: "none", borderRadius: "50%", width: 16, height: 16, cursor: "pointer", color: "#fff", fontSize: 9, lineHeight: "16px", textAlign: "center", padding: 0 }}>✕</button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {generatedAssets.length > 0 ? (
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8, maxHeight: 200, overflowY: "auto" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(54px, 1fr))", gap: 4 }}>
                      {generatedAssets.map(a => {
                        const url = generatedAssetUrls[a.id];
                        const selected = compositorAssets.includes(a.id);
                        return (
                          <div key={a.id} onClick={() => {
                            if (selected) setCompositorAssets(prev => prev.filter(x => x !== a.id));
                            else setCompositorAssets(prev => [...prev, a.id]);
                          }} style={{ position: "relative", aspectRatio: "3/4", cursor: "pointer", border: selected ? "2px solid #2d6a4f" : "2px solid transparent", borderRadius: 4, overflow: "hidden", opacity: selected ? 1 : 0.65 }}>
                            {url
                              ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block" }} />
                              : <div style={{ width: "100%", height: "100%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#9ca3af" }}>⏳</div>
                            }
                            {selected && <div style={{ position: "absolute", bottom: 2, right: 2, background: "#2d6a4f", borderRadius: "50%", width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff" }}>✓</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : !wallLoading ? (
                  <div style={{ fontSize: 12, color: "#aaa", padding: "6px 0" }}>Sin imágenes generadas. Las tomas de sesiones aparecerán aquí.</div>
                ) : null}
              </div>

              {/* Caption */}
              <label style={{ ...lbl, marginTop: 14 }}>Caption
                <textarea
                  value={compositorForm.caption}
                  onChange={e => setCompositorForm(f => ({ ...f, caption: e.target.value }))}
                  style={{ ...inp, height: 80, resize: "vertical" }}
                  placeholder="Texto de la publicación…"
                  maxLength={2200}
                />
                <span style={{ fontSize: 11, color: "#aaa", textAlign: "right" }}>{compositorForm.caption.length}/2200</span>
              </label>

              {/* Audio mode */}
              <label style={{ ...lbl, marginTop: 10 }}>Modo de audio
                <select value={compositorForm.audioMode} onChange={e => setCompositorForm(f => ({ ...f, audioMode: e.target.value as typeof compositorForm.audioMode }))} style={inp}>
                  <option value="SILENT_EXPORT">Sin audio (exportación silenciosa)</option>
                  <option value="LICENSED_TRACK">Pista licenciada</option>
                  <option value="PLATFORM_LATER">Añadir audio en la plataforma</option>
                </select>
              </label>

              {/* Schedule */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 13, color: "#444", fontWeight: 500, marginBottom: 6 }}>Programar (opcional)</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <label style={{ ...lbl, flex: "1 1 180px" }}>Fecha y hora local
                    <input type="datetime-local" value={compositorForm.scheduleDate} onChange={e => setCompositorForm(f => ({ ...f, scheduleDate: e.target.value }))} style={inp} />
                  </label>
                  <label style={{ ...lbl, flex: "1 1 180px" }}>Zona horaria
                    <input type="text" value={compositorForm.scheduleTz} onChange={e => setCompositorForm(f => ({ ...f, scheduleTz: e.target.value }))} style={inp} placeholder="Europe/Madrid" />
                  </label>
                </div>
                {compositorForm.scheduleDate && (
                  <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Se programará en estado SCHEDULED si la cuenta tiene ruta de publicación activa.</div>
                )}
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button type="submit" disabled={creatingPub} style={{ ...btnPrim, opacity: creatingPub ? 0.6 : 1 }}>
                  {creatingPub ? "Creando…" : compositorForm.scheduleDate ? "Crear y programar" : "Guardar borrador"}
                </button>
                <button type="button" onClick={() => { setShowCompositor(false); setCompositorAssets([]); }} style={btnSec}>Cancelar</button>
              </div>
            </form>
          )}

          {/* Publications list */}
          {pubsLoading ? (
            <div style={{ color: "#888", fontSize: 13, padding: "24px 0" }}>Cargando publicaciones…</div>
          ) : publications.length === 0 && !showCompositor ? (
            <div style={{ background: "#fff", borderRadius: 10, padding: 40, textAlign: "center", color: "#888" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Sin publicaciones todavía</div>
              <div style={{ fontSize: 13 }}>Usa el compositor para crear tu primera publicación para este influencer.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {publications.map(p => {
                const STATE_COLORS: Record<string, string> = {
                  DRAFT: "#6366f1", NEEDS_REVIEW: "#d97706", APPROVED: "#15803d",
                  SCHEDULED: "#1d4ed8", CONFIRMED_MANUAL: "#15803d", PUBLISHED: "#15803d",
                  FAILED: "#dc2626", CANCELLED: "#9ca3af", BLOCKED_ROUTE: "#dc2626",
                  SENDING: "#0891b2", EXPORTED: "#0891b2", UNKNOWN_EXTERNAL: "#9ca3af",
                };
                const sc = STATE_COLORS[p.state] ?? "#9ca3af";
                const account = p.account_id ? socialAccounts.find(a => a.accountId === p.account_id) : null;
                return (
                  <div key={p.id} style={{ background: "#fff", borderRadius: 8, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: sc + "22", color: sc, fontWeight: 600 }}>{p.state}</span>
                        {account && (
                          <span style={{ fontSize: 11, color: "#888" }}>{PLATFORM_LABELS[account.platform] ?? account.platform} · @{account.displayName ?? account.externalId}</span>
                        )}
                        {p.planned_at && (
                          <span style={{ fontSize: 11, color: "#888" }}>{new Date(p.planned_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: "#333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 480 }}>
                        {p.caption || <span style={{ color: "#aaa", fontStyle: "italic" }}>Sin caption</span>}
                      </div>
                      <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                        {new Date(p.created_at).toLocaleDateString("es-ES")} · rev.{p.revision}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Estrategia tab */}
      {tab === "estrategia" && (
        <div style={{ background: "#fff", padding: 24, borderRadius: 10, color: "#666" }}>
          <p>Ver portfolio y estrategia en la sección <Link to="/costs" style={{ color: "#2d6a4f" }}>Costes</Link>.</p>
          <p>Las ideas y sesiones están disponibles en sus secciones correspondientes.</p>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 16, padding: "8px 0", borderBottom: "1px solid #f0ece4" }}>
      <span style={{ fontSize: 13, color: "#888", width: 120, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#1a1a1a" }}>{value}</span>
    </div>
  );
}

const btnPrim: React.CSSProperties = { padding: "8px 20px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const btnSec: React.CSSProperties = { padding: "8px 16px", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", background: "none", fontSize: 13 };
const btnLinkStyle: React.CSSProperties = { padding: "6px 14px", background: "#2d6a4f", color: "#fff", textDecoration: "none", borderRadius: 6, fontSize: 13, fontWeight: 600 };
const inp: React.CSSProperties = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
const lbl: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#444" };
